import { useEffect, useRef, useState } from 'react';
import type { MouseEvent, PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { C, F, M, dateKeyInTz, dayMonthLabel, fmtTimeInTz, isPending, weekdayLabel, zonedDayRangeUtc } from './theme';
import { externalAircraftLabel } from '../../lib/opsBoardTypes';
import type { Aircraft, Asset, Base, Booking, BookingKind, RowFilters, Staff, TrainingActivity } from '../../lib/opsBoardTypes';
import { createPortal } from 'react-dom';
import { bookingLabel, taskRowLabel, truncateToWidth } from '../../lib/opsBoard/bookingLabel';
import {
  FILL_ALPHA, TOP_BAR_H, chipInstructorColor, counterpartyName, leadParticipant,
  personSurname, type ChipChannels, type ChipChannelSource,
} from '../../lib/opsBoard/chipChannels';
import { useIsMobile } from '../../lib/useIsMobile';
import { chipVisual, type ChipState } from '../../lib/opsBoard/chipVisual';
import { notifyWarning, notifyWarningText } from '../../lib/notifyWarning';

const UNAVAIL_COLOR = '#94a3b8'; // slate — the seeded "Unavailable" kind colour (Feature B)

interface Props {
  // A "section" of the board: a single base, or (base === null) the off-base group.
  base: Base | null;
  allBases: boolean;
  showHeader: boolean;
  date: string;             // selected day (YYYY-MM-DD) — for multi-day clamping
  bookings: Booking[];      // ALL tenant bookings overlapping the visible day (across bases)
  aircraft: Aircraft[];
  assets: Asset[];
  staff: Staff[];
  bases: Base[];
  bookingKinds: BookingKind[];
  activities: TrainingActivity[];
  // Active (un-returned) maintenance bookings from the wider fetched set — used to resolve the
  // post-ECD "tail" (which falls outside the day's booking pool) to its booking for a clickable
  // confirm-return. May not include far-overdue bookings outside the fetch window (degraded tail).
  activeMaint: Booking[];
  filters: RowFilters;
  canWorkflow: boolean;     // ops/admin → relocate affordance
  showCancelled: boolean;   // Step 5c.3 — render cancelled bookings (greyed) when on
  changedIds: Set<string>;  // Step 5c.3 — bookings whose updated_at just changed (brief highlight)
  waitlist: Booking[];      // Step 5c.3 — waitlisted bookings overlapping this day (across bases)
  unassigned: Booking[];    // Recurring Phase 2 — confirmed series occurrences with no crew/allocation, overlapping this day
  canBook: boolean;         // booking-write rights (instructor/ops/admin) — gates drag handles (PR-000031)
  // 0.12.4 — the tenant's chip colour channels, ALREADY RESOLVED. DayView is deliberately handed
  // the resolved pair rather than `settings`: the translation from the settings spelling
  // ('booking_kind') to the render spelling ('kind') happens exactly once, in
  // chipChannels.resolveChipChannels, and never in a renderer. Two DayViews mount per board
  // section, so resolving here would also repeat the work per section.
  chipChannels: ChipChannels;
  onBookingClick: (b: Booking) => void;
  onSlotClick: (slot: { baseId: string; aircraftReg?: string; assetId?: string; pilotId?: string; startTime: string }) => void;
  onRelocate: (reg: string) => void;
  // PR-000031 — commit a confirmed edge-drag reschedule (normalized start+finish write).
  // Resolves ok:false on a rejection (caller surfaces the plain-English reason; chip snaps back here).
  onReschedule: (b: Booking, patch: { block_start_at: string; arr_time_planned: string; activity_mins: number }) => Promise<{ ok: boolean }>;
  // PR-000031 — true while a drag / its confirm dialog is live, so the board can pause the poll
  // tick (whose ref refresh unmounts the sections and would wipe the gesture mid-flight).
  onDragActiveChange: (active: boolean) => void;
}

const START = 0;
const TOTAL = 24; // 00:00 → 24:00 — the FULL day (Stage 2): after-hours tasks render at their
                  // true position instead of clamping to a 06:00–21:00 window; office-closed
                  // hours stay visible but shaded (outsideShade).
const SLOT_W = 50;
const LABEL_W = 156;
const ROW_H = 60;

// PR-000058 — the three chip text rows. Baselines sit inside the chip body, which runs y 4 to
// rowH-4 (56 at ROW_H 60); the 7.5px row three needs its DESCENDER inside that, not just its
// baseline. ⚠ The px-per-char budgets are MEASURED from the rendered DOM, not guessed — see
// database/verify-chip-rows.ts, which re-measures them against the real font stack.
const ROW1_Y = 22;   // time + status word
const ROW2_Y = 36;   // the person
const ROW3_Y = 47;   // the task
const NAME_PX = 10;   // row two font size
const TASK_PX = 7.5;  // row three font size
const NAME_PX_PER_CHAR = 5.6;  // measured for 10px Inter-ish at weight 600
const TASK_PX_PER_CHAR = 4.2;  // measured for 7.5px
const TEXT_LEFT_PAD = 12;      // rows two and three start at textX and get nearly the full width

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

// With the track starting at 00:00, a fresh board would open staring at midnight. OpsBoard calls
// this on the first day-view render to scroll the board column so the operating day starts at the
// left of the track area instead (scrollLeft = t2x(open); with START=0 that is open × SLOT_W).
// Geometry lives here with the other track constants. Falls back to 06:00 when the base has no
// configured opening time (or none parses).
export function defaultBoardScrollLeft(operatingHoursStart: string | null | undefined): number {
  const h = operatingHoursStart
    ? parseInt(operatingHoursStart.slice(0, 2), 10) + parseInt(operatingHoursStart.slice(3, 5), 10) / 60
    : 6;
  return (Number.isFinite(h) ? clamp(h, 0, TOTAL) : 6) * SLOT_W;
}

function ddMMMyy(iso: string): string {
  const d = new Date(iso);
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getUTCMonth()];
  return `${String(d.getUTCDate()).padStart(2, '0')}${mon}${String(d.getUTCFullYear()).slice(2)}`;
}

function bookingEndMs(b: Booking): number {
  const s = new Date(b.block_start_at!).getTime();
  const planned = b.arr_time_planned ? new Date(b.arr_time_planned).getTime() : s + (b.activity_mins ?? 60) * 60_000;
  // A confirmed-returned maintenance block displays to its ACTUAL return: a LATE return EXTENDS the
  // drawn window past the planned ECD; an EARLY return keeps the planned end so the faint
  // "returned early" remainder (returned→ECD) still draws. §B/buffer use the server-side
  // effectiveBlockEnd (LEAST(planned, returned)), not this display end — display only.
  if (b.maintenance_returned_at) return Math.max(planned, new Date(b.maintenance_returned_at).getTime());
  return planned;
}

// PR-000031 — the PLANNED window end (arr ?? start + activity_mins; mirrors the backend
// blockEndInstant). Distinct from bookingEndMs, which is display-only and extends a late
// maintenance return — drag math must run on the formal window, never the display window.
function plannedEndMs(b: Booking): number {
  const s = new Date(b.block_start_at!).getTime();
  return b.arr_time_planned ? new Date(b.arr_time_planned).getTime() : s + (b.activity_mins ?? 60) * 60_000;
}

// ── PR-000031 Phase 1: edge-drag time adjustment ──────────────────────────────
const SNAP_HOURS = 0.25;              // 15-min board snap (the form keeps 5-min resolution)
const MIN_DURATION_MS = 15 * 60_000;  // one board slot — finish can never reach the start
const HANDLE_HIT_W = 20;              // finger-friendly hit area; the visual grip is slimmer

interface DragState {
  booking: Booking;         // as it was when the drag started (orig times captured below)
  edge: 'start' | 'finish';
  tz: string;               // display tz of the dragged chip's row (section tz; dep tz off-base)
  dayStartMs: number;       // UTC instant of the viewed day's local midnight in tz
  trackLeft: number;        // clientX of track x=0, captured at drag start
  origStartMs: number;
  origEndMs: number;        // planned end (plannedEndMs)
  ghostMs: number;          // current snapped position of the dragged edge
}

interface ConfirmDragState {
  booking: Booking;
  edge: 'start' | 'finish';
  tz: string;
  origStartMs: number;
  origEndMs: number;
  newEdgeMs: number;
}

export default function DayView(props: Props) {
  const { base, allBases, showHeader, date, bookings, aircraft, assets, staff, bases, bookingKinds, activities, activeMaint, filters, canWorkflow, showCancelled, changedIds, waitlist, unassigned, canBook, chipChannels, onBookingClick, onSlotClick, onRelocate, onReschedule, onDragActiveChange } = props;
  const isMobile = useIsMobile();

  // Resource rows show only bookings that OCCUPY a line (not parked on the waitlist).
  const rowPool = bookings.filter((b) => !b.waitlisted);

  const totalW = TOTAL * SLOT_W;
  const hours = Array.from({ length: TOTAL + 1 }, (_, i) => i + START);
  const t2x = (t: number) => ((t - START) / TOTAL) * totalW;

  const tenantIcaos = new Set(bases.map((b) => b.icao).filter((x): x is string => !!x));
  const baseById = new Map(bases.map((b) => [b.id, b]));
  const baseByIcao = new Map(bases.filter((b) => b.icao).map((b) => [b.icao as string, b]));
  const kindsById = new Map(bookingKinds.map((k) => [k.id, k]));
  const activitiesById = new Map(activities.map((a) => [a.id, a]));
  // PR-000059 — the instructor colour is resolved from the STAFF array, not from the booking.
  // DayView already receives staff for the instructor rows, so this is a map read with no
  // payload change; putting the colour on every booking participant would inflate every board
  // fetch and every cached row for nothing.
  const staffById = new Map(staff.map((s) => [s.id, s]));
  const basesById = new Map(bases.map((b) => [b.id, b]));
  // First active (un-returned) maintenance booking per aircraft reg — resolves the post-ECD tail.
  const maintByReg = new Map<string, Booking>();
  for (const b of activeMaint) {
    for (const a of b.asset_allocations) {
      if (a.aircraft_registration && !maintByReg.has(a.aircraft_registration)) maintByReg.set(a.aircraft_registration, b);
    }
  }
  const instructorBaseId = (s: Staff): string | null => s.current_base_id ?? s.home_base_id ?? null;
  const aircraftResidentBaseIcao = (a: Aircraft): string | null => (a.current_base_icao && tenantIcaos.has(a.current_base_icao) ? a.current_base_icao : null);
  const offBase = base === null;
  const sectionTz = base?.timezone ?? 'Australia/Darwin';

  const slotTimeFromClick = (e: MouseEvent<HTMLDivElement>): string => {
    const rect = e.currentTarget.getBoundingClientRect();
    const h = START + ((e.clientX - rect.left) / totalW) * TOTAL;
    // Upper clamp is one slot INSIDE the track end: with a 24h track the rightmost pixel would
    // otherwise produce "24:00", which is not a valid start time (the old 15h track's edge gave
    // a valid "21:00", so this never mattered before).
    const hh = Math.max(START, Math.min(START + TOTAL - 0.5, Math.floor(h * 2) / 2));
    return `${String(Math.floor(hh)).padStart(2, '0')}:${hh % 1 ? '30' : '00'}`;
  };

  // Multi-day-aware span for the selected day in `tz` (AU tenant zones have no DST,
  // so elapsed-ms-from-local-midnight == local hour-of-day).
  const spanForDay = (b: Booking, tz: string): { startH: number; durH: number; contPrev: boolean; contNext: boolean } | null => {
    if (!b.block_start_at) return null;
    const { from, to } = zonedDayRangeUtc(date, tz);
    const dayStart = new Date(from).getTime();
    const dayEnd = new Date(to).getTime();
    const s = new Date(b.block_start_at).getTime();
    const e = bookingEndMs(b);
    if (e <= dayStart || s >= dayEnd) return null; // not on this day
    const vs = Math.max(s, dayStart);
    const ve = Math.min(e, dayEnd);
    return {
      startH: (vs - dayStart) / 3_600_000,
      durH: Math.max(0.15, (ve - vs) / 3_600_000),
      contPrev: s < dayStart,
      contNext: e > dayEnd,
    };
  };

  const allocHasAircraft = (b: Booking, reg: string) => b.asset_allocations.some((a) => a.aircraft_registration === reg);
  const allocHasAsset = (b: Booking, id: string) => b.asset_allocations.some((a) => a.asset_id === id);
  // A person's row shows every booking they participate in (any role) — Step 5c.3.
  const isStaffOn = (b: Booking, id: string) => b.participants.some((p) => p.user_id === id);
  const isMaintBooking = (b: Booking) => !!(b.booking_kind_id && kindsById.get(b.booking_kind_id)?.is_maintenance);

  // ── PR-000031: chip selection + edge-drag (per-section state) ────────────────
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [confirmDrag, setConfirmDrag] = useState<ConfirmDragState | null>(null);
  const [dragSubmitting, setDragSubmitting] = useState(false);
  const dragRef = useRef<DragState | null>(null);
  useEffect(() => { dragRef.current = drag; }, [drag]);
  // A drag's trailing click event must not open/create/deselect anything. Cleared on the
  // next macrotask so a swallowed-elsewhere click can never eat a later legitimate one.
  const suppressClickRef = useRef(false);

  // Day navigation mid-interaction: drop any selection/drag/confirm (geometry is day-anchored).
  useEffect(() => { setSelectedId(null); setDrag(null); setConfirmDrag(null); }, [date]);

  // Report drag/confirm liveness so OpsBoard pauses its poll tick (which would otherwise unmount
  // this section mid-gesture). Only transitions are reported, and an unmount while live reports
  // false — so an idle section can never clobber another section's active drag.
  const interactionActive = drag !== null || confirmDrag !== null;
  // PR-000058 D3 — GATE THE HOVER CARD ON THE POINTER, NEVER ON THE WIDTH. A mouse or trackpad
  // on any OS at any window size gets the card; touch gets nothing and a tap still opens the
  // detail exactly as today. An iPad with a trackpad attached gets it, which a width rule could
  // never work out, and rotation is irrelevant because the pointer does not change when the
  // device turns. Listened to, not read once: a trackpad can be attached mid-session.
  const [canHover, setCanHover] = useState(() => typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(hover: hover)');
    const on = () => setCanHover(mq.matches);
    mq.addEventListener('change', on);
    setCanHover(mq.matches);
    return () => mq.removeEventListener('change', on);
  }, []);
  const [hoverCard, setHoverCard] = useState<{ x: number; y: number; lines: string[] } | null>(null);
  // ⚠ Drop the card whenever the board data changes. onMouseLeave does NOT fire if the chip
  // unmounts under the pointer — a poll tick, a date change or a filter toggle all do that — and
  // a card left behind would keep showing details for a booking that may no longer be there.
  // On an ops board a stale readout is worse than no readout.
  useEffect(() => { setHoverCard(null); }, [bookings, filters, date]);
  const reportedActiveRef = useRef(false);
  useEffect(() => {
    if (interactionActive !== reportedActiveRef.current) {
      reportedActiveRef.current = interactionActive;
      onDragActiveChange(interactionActive);
    }
  }, [interactionActive, onDragActiveChange]);
  useEffect(() => () => { if (reportedActiveRef.current) { reportedActiveRef.current = false; onDragActiveChange(false); } }, [onDragActiveChange]);

  // Draggable = ordinary fleet/asset/instructor/external booking the user may edit. Excluded (v1):
  // cancelled, waitlisted (own lane), maintenance (its window drives the aircraft ECD via
  // maintenance_mark_unserviceable — form-only for now), provisional/queued rows (edit via the
  // form; a queued create has no server id), conflicted rows (resolve first). Mirror / unassigned
  // chips never reach this predicate (their renderers add no handles); external chips joined the
  // shared selection + drag machinery in Stage 2 — an external-linked booking was already
  // draggable from its other chips, so this only removed a UI inconsistency. Permission mirrors
  // the backend canEditAll rule: the whole write tier edits ANY booking (PR-000038), so canBook
  // alone gates the handles.
  const canDragBooking = (b: Booking): boolean =>
    !!b.block_start_at
    && b.booking_status !== 'cancelled'
    && !b.waitlisted
    && !isMaintBooking(b)
    && !b._pending_op
    && b.sync_state !== 'conflicted'
    && canBook;

  // Live ghost window for the booking being dragged / awaiting confirm — applied to EVERY chip
  // of that booking in this section, so all its chips move together.
  const ghost = drag
    ? { bookingId: drag.booking.id, startMs: drag.edge === 'start' ? drag.ghostMs : drag.origStartMs, endMs: drag.edge === 'finish' ? drag.ghostMs : drag.origEndMs }
    : confirmDrag
      ? { bookingId: confirmDrag.booking.id, startMs: confirmDrag.edge === 'start' ? confirmDrag.newEdgeMs : confirmDrag.origStartMs, endMs: confirmDrag.edge === 'finish' ? confirmDrag.newEdgeMs : confirmDrag.origEndMs }
      : null;

  // Pointer x → snapped edge instant, clamped to the visible track (= the handle's own day,
  // decision 2) and to the one-slot minimum duration against the booking's OTHER edge. With the
  // full-day track the clamp is [00:00, 24:00], so a move-whole can no longer push the un-dragged
  // edge past the visible window (the 0.9.0 cosmetic nit).
  const snapEdgeMs = (clientX: number, d: DragState): number => {
    const h = START + ((clientX - d.trackLeft) / totalW) * TOTAL;
    const snapped = clamp(Math.floor(h / SNAP_HOURS) * SNAP_HOURS, START, START + TOTAL);
    const ms = d.dayStartMs + snapped * 3_600_000;
    return d.edge === 'start' ? Math.min(ms, d.origEndMs - MIN_DURATION_MS) : Math.max(ms, d.origStartMs + MIN_DURATION_MS);
  };

  const onHandleDown = (e: ReactPointerEvent<SVGGElement>, b: Booking, edge: 'start' | 'finish', tz: string) => {
    e.stopPropagation();
    e.preventDefault();
    // Capture so the trailing click targets the handle (whose onClick swallows it), not a row.
    try { (e.target as Element).setPointerCapture(e.pointerId); } catch { /* unsupported — suppressClickRef covers it */ }
    const svg = e.currentTarget.ownerSVGElement;
    if (!svg) return;
    setDrag({
      booking: b,
      edge,
      tz,
      dayStartMs: new Date(zonedDayRangeUtc(date, tz).from).getTime(),
      trackLeft: svg.getBoundingClientRect().left,
      origStartMs: new Date(b.block_start_at!).getTime(),
      origEndMs: plannedEndMs(b),
      ghostMs: edge === 'start' ? new Date(b.block_start_at!).getTime() : plannedEndMs(b),
    });
  };

  const dragging = drag !== null;
  useEffect(() => {
    if (!dragging) return;
    const move = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const ms = snapEdgeMs(e.clientX, d);
      if (ms !== d.ghostMs) setDrag({ ...d, ghostMs: ms });
    };
    const up = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      suppressClickRef.current = true;
      window.setTimeout(() => { suppressClickRef.current = false; }, 0);
      setDrag(null);
      const ms = snapEdgeMs(e.clientX, d);
      const origEdge = d.edge === 'start' ? d.origStartMs : d.origEndMs;
      if (ms === origEdge) return; // dropped where it started — nothing to confirm
      setConfirmDrag({ booking: d.booking, edge: d.edge, tz: d.tz, origStartMs: d.origStartMs, origEndMs: d.origEndMs, newEdgeMs: ms });
    };
    const cancel = () => setDrag(null);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging]);

  // Chip click: first tap SELECTS a draggable chip (reveals handles); tapping the selected chip
  // opens the detail panel (the panel stays reachable). Non-draggable chips keep single-tap
  // detail-open; tapping any other chip re-targets/clears the selection.
  const onChipClick = (e: MouseEvent, b: Booking) => {
    e.stopPropagation();
    if (suppressClickRef.current) { suppressClickRef.current = false; return; }
    if (selectedId !== b.id && canDragBooking(b)) { setSelectedId(b.id); return; }
    if (selectedId !== b.id) setSelectedId(null);
    onBookingClick(b);
  };

  // Empty-track click: with a chip selected it only DESELECTS (never falls through to
  // create-a-booking); with nothing selected it behaves exactly as before.
  const trackClick = (slot: { baseId: string; aircraftReg?: string; assetId?: string; pilotId?: string; startTime: string }) => {
    if (suppressClickRef.current) { suppressClickRef.current = false; return; }
    if (selectedId) { setSelectedId(null); return; }
    onSlotClick(slot);
  };

  // Edge handle — ~20px hit area (finger-friendly), slim visual grip. touchAction:none keeps a
  // touch-drag from starting the board's pan-scroll; pan starts on empty track, resize here.
  const handleEl = (hx: number, edge: 'start' | 'finish', b: Booking, tz: string, rowH: number) => (
    <g
      key={`h-${edge}`}
      onPointerDown={(e) => onHandleDown(e, b, edge, tz)}
      onClick={(e) => e.stopPropagation()}
      style={{ cursor: 'ew-resize', touchAction: 'none' }}
    >
      <rect x={hx - HANDLE_HIT_W / 2} y={2} width={HANDLE_HIT_W} height={rowH - 4} fill="transparent" />
      <rect x={hx - 3} y={10} width={6} height={rowH - 20} rx={3} fill={C.accent} stroke="#fff" strokeWidth={1.2} />
    </g>
  );

  // "11:00" for a same-day instant; "11:00 Wed 16 Jul" once it leaves the viewed day.
  const fmtEdgeInTz = (ms: number, tz: string): string => {
    const iso = new Date(ms).toISOString();
    const day = dateKeyInTz(iso, tz);
    const t = fmtTimeInTz(iso, tz);
    return day === date ? t : `${t} ${weekdayLabel(day)} ${dayMonthLabel(day)}`;
  };
  const fmtDurMins = (mins: number): string => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h === 0 ? `${m}m` : m === 0 ? `${h}h` : `${h}h ${m}m`;
  };

  // Cross-midnight delineation (Stage 2) — the chip side that continues into an adjacent day gets
  // a hatched day-boundary cap + a solid boundary line + a chevron, plus the TRUE edge's time and
  // weekday ("→ 01:00 Wed" / "23:00 Tue ←"), replacing the old 8px "continued →/→ continues" text.
  // Render-only (pointerEvents none); drag handles stay suppressed on a continuing side via the
  // existing !contPrev/!contNext rule, so the cap never sits under a handle.
  const CAP_W = 8;
  const boundaryCap = (side: 'prev' | 'next', x0: number, w: number, rowH: number, edgeMs: number, tz: string) => {
    const iso = new Date(edgeMs).toISOString();
    const edgeLabel = `${fmtTimeInTz(iso, tz)} ${weekdayLabel(dateKeyInTz(iso, tz))}`;
    const cx = side === 'prev' ? x0 : x0 + w - CAP_W;
    const bx = side === 'prev' ? x0 : x0 + w; // the day-boundary edge itself
    return (
      <g pointerEvents="none">
        <rect x={cx} y={4} width={CAP_W} height={rowH - 8} fill="url(#dayBoundaryHatch)" />
        <line x1={bx} y1={4} x2={bx} y2={rowH - 4} stroke="#475569" strokeWidth={1.5} />
        <text
          x={cx + CAP_W / 2} y={Math.round(rowH / 2) + 3} textAnchor="middle"
          fill="#334155" stroke="#fff" strokeWidth={2} paintOrder="stroke"
          fontSize={9} fontWeight={700} fontFamily={M}
        >{side === 'prev' ? '«' : '»'}</text>
        {/* Width guard is deliberately low: the COMMON cross-midnight tail is one hour (50px) and
            must still carry its label — a few px of overflow past the non-boundary side is fine
            (SVG text isn't clipped and the label sits on the bottom line, clear of the headline). */}
        {w > 40 && (
          <text
            x={side === 'prev' ? x0 + CAP_W + 3 : x0 + w - CAP_W - 3} y={rowH - 8}
            textAnchor={side === 'prev' ? 'start' : 'end'}
            fill={C.textDim} fontSize={8} fontFamily={M}
          >{side === 'prev' ? `${edgeLabel} ←` : `→ ${edgeLabel}`}</text>
        )}
      </g>
    );
  };

  // ── Block renderers ──────────────────────────────────────────────────────────

  const bookingRect = (ob: Booking, rowH: number, displayTz: string) => {
    // PR-000031: while an edge-drag (or its confirm) is live, render this booking's chips at the
    // ghost window — every chip of the booking moves together. Callbacks keep the ORIGINAL row.
    const b: Booking = ghost && ghost.bookingId === ob.id
      ? { ...ob, block_start_at: new Date(ghost.startMs).toISOString(), arr_time_planned: new Date(ghost.endMs).toISOString() }
      : ob;
    const span = spanForDay(b, displayTz);
    if (!span) return null;
    const x0 = clamp(t2x(span.startH), 0, totalW);
    const x1 = clamp(t2x(span.startH + span.durH), 0, totalW);
    const w = Math.max(6, x1 - x0);
    const pending = isPending(b.approval_status);
    const declined = b.approval_status === 'declined';
    const cancelled = b.booking_status === 'cancelled';
    const maint = isMaintBooking(b);
    const changed = changedIds.has(b.id);
    const kind = b.booking_kind_id ? kindsById.get(b.booking_kind_id) : undefined;
    const kindColor = kind?.ui_color;
    const unavail = !!kind?.is_unavailability; // Feature B — slate, muted resource blocker
    // PR-000058/059 — the two configurable colour channels, now read from company settings and
    // handed down already resolved (`chipChannels` prop). The third channel, confirmation
    // status, is the left stripe and is not configurable.
    //
    // ⚠ EVERY ARM IS EXPLICIT AND THE LAST ONE IS null, NOT THE BASE COLOUR. This used to end
    // `: basesById.get(...)`, which made "anything I do not recognise" mean "colour by base" —
    // silently, and plausibly enough that nobody would spot it on a board. An unknown source now
    // renders UNCOLOURED, which is visible. resolveChipChannels should make that unreachable;
    // this is the second guarantee, not the first.
    const channelColor = (src: ChipChannelSource): string | null =>
      src === 'kind' ? (kindColor ?? null)
      : src === 'instructor' ? chipInstructorColor(b, staffById)
      : src === 'base' ? (basesById.get(b.base_id)?.ui_color ?? null)
      : null;
    const fillColor = channelColor(chipChannels.fill);
    const topBarColor = channelColor(chipChannels.topBar);
    // 5d-p2: queued-on-this-device (provisional) and replayed-but-conflicted treatments.
    const localPending = !!b._pending_op;
    const conflicted = !localPending && b.sync_state === 'conflicted';

    // Confirmed-returned maintenance (durable record). The SOLID chip is resized to the ACTUAL
    // window start→returned_at (early shrinks, late extends — see bookingEndMs). For an EARLY
    // return the remainder returned_at→planned ECD drops into the background as faint "returned
    // early" history. No expected-completion tail (it's resolved). §B/buffer excluded server-side.
    if (maint && b.maintenance_returned_at) {
      const { from } = zonedDayRangeUtc(date, displayTz);
      const dayStartMs = new Date(from).getTime();
      const returnedH = (new Date(b.maintenance_returned_at).getTime() - dayStartMs) / 3_600_000;
      const rx = clamp(t2x(returnedH), x0, x1);
      const firmW = Math.max(0, rx - x0);
      const histW = Math.max(0, x1 - rx);
      const col = kindColor ?? C.accent;
      const plannedMs = b.arr_time_planned ? new Date(b.arr_time_planned).getTime() : new Date(b.block_start_at!).getTime();
      const retMs = new Date(b.maintenance_returned_at).getTime();
      const word = retMs < plannedMs ? '🔧 returned early' : retMs > plannedMs ? '🔧 returned (late)' : '🔧 returned';
      return (
        <g key={b.id} onClick={(e) => { e.stopPropagation(); setSelectedId(null); onBookingClick(ob); }} style={{ cursor: 'pointer' }}>
          {firmW > 0 && (
            <rect x={x0} y={4} width={firmW} height={rowH - 8} fill={col} stroke={col} strokeWidth={1.5} rx={4} opacity={1} />
          )}
          {histW > 0 && (
            <rect x={rx} y={6} width={histW} height={rowH - 12} fill="#f1f5f9" stroke={C.borderMed} strokeWidth={1} strokeDasharray="3,3" rx={3} opacity={0.5} />
          )}
          {firmW > 56 && (
            <text x={x0 + 6} y={22} fill="#fff" fontSize={9} fontWeight={700} fontFamily={F}>{word}</text>
          )}
        </g>
      );
    }

    // E1 (LOCKED, AMENDED BY PR-000058/059): status is a solid LEFT stripe — that part is
    // unchanged and was already shipped. What changed on 2026-09-04 is the other two channels:
    // the booking TYPE moved off the fill and onto its own TOP BAR, and the fill it vacated now
    // carries the INSTRUCTOR. E1 locked "type in the fill"; Ben superseded that deliberately.
    //
    // ⚠ The five special states (provisional/conflict/cancelled/maintenance/unavailable) keep
    // their own fill+border and get NO status stripe. That is unchanged, and it has a
    // consequence worth stating rather than discovering: ON THOSE CHIPS THE INSTRUCTOR COLOUR
    // IS NOT SHOWN. That is the right priority — those chips are saying something more urgent
    // than who is flying — and it is the same reasoning that already suppresses the stripe.
    // The TOP BAR still renders on them, because the type is true regardless of status.
    // PR-000059: the fill carries the CONFIGURED CHANNEL — the instructor by default. The booking
    // type moved OFF the fill and onto its own top bar, which is what frees this one. Status
    // remains the solid left stripe.
    //
    // ⚠ THE DECISION LIVES IN lib/opsBoard/chipVisual.ts, NOT HERE, and it is not a tidy-up.
    // DayViewLegend has to paint the same swatches, and when it approximated them by hand it
    // drifted — it spent the whole of 0.12.3 describing the pre-0.12.3 scheme. One function, two
    // callers, so the legend cannot fall behind the chip again.
    const chipState: ChipState =
      localPending ? 'provisional'
      : conflicted ? 'conflict'
      : cancelled ? 'cancelled'
      : unavail ? 'unavailable'
      : maint && kindColor ? 'maintenance'
      : 'ordinary';
    const vis = chipVisual({
      state: chipState, fillColor: fillColor ?? null, kindColor: kindColor ?? null,
      unavailColor: UNAVAIL_COLOR, fillAlpha: FILL_ALPHA,
      pending, declined, outsideHours: !!b.outside_hours,
      confirmed: b.booking_status === 'confirmed', C,
    });
    const { bg, border, statusStripe } = vis;
    // Headline (time) colour — white on the solid maintenance fill, status-coloured for ordinary,
    // muted slate for unavailable.
    const headColor = localPending ? '#7c3aed' : conflicted ? C.red : unavail ? '#64748b' : maint && kindColor ? '#fff' : pending ? C.amber : declined ? C.red : C.green;

    const isSel = selectedId === ob.id;
    const dragOk = canDragBooking(ob);
    // PR-000058 part D — the FULL text, untruncated, for both hover tiers. Plain hyphen in the
    // time range: the standing rule is no en or em dashes in user-facing copy.
    const hoverLines: string[] = [
      `${fmtTimeInTz(b.block_start_at!, displayTz)} - ${fmtTimeInTz(new Date(bookingEndMs(b)).toISOString(), displayTz)}`,
      counterpartyName(b, 'student'),
      (() => { const i = counterpartyName(b, 'instructor'); return i ? `Instructor: ${i}` : null; })(),
      // ⚠ taskRowLabel, not bookingLabel, and for the SAME reason as row three: this array
      // already carries the person on its own line above, so bookingLabel would repeat it. This
      // one array feeds BOTH hover tiers — the SVG <title> and the tier-two card — so the
      // duplication was on both. Blank entries are dropped by the filter below.
      taskRowLabel(b, kindsById, activitiesById),
    ].filter((x): x is string => !!x);
    return (
      <g
        key={b.id}
        onClick={(e) => onChipClick(e, ob)}
        style={{ cursor: 'pointer' }}
        onMouseEnter={canHover ? (e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setHoverCard({ x: r.left, y: r.bottom, lines: hoverLines });
        } : undefined}
        onMouseLeave={canHover ? () => setHoverCard(null) : undefined}
      >
        {/* D1 — tier one: a native SVG <title>. Free, unstyleable and slow to appear, which is
            why tier two exists, but it works everywhere including touch and costs nothing. */}
        <title>{hoverLines.join('\n')}</title>
        {!maint && !cancelled && (() => {
          const bp = b.buf_post_mins ?? 30;
          if (bp <= 0 || span.contNext) return null;
          const bufW = (bp / 60 / TOTAL) * totalW;
          return <rect x={x0 + w} y={4} width={Math.min(bufW, totalW - (x0 + w))} height={rowH - 8} fill="url(#bufHatch)" rx={2} opacity={0.85} />;
        })()}
        <rect
          x={x0} y={4} width={w} height={rowH - 8}
          fill={bg} stroke={border} strokeWidth={changed ? 2.5 : vis.strokeWidth}
          strokeDasharray={vis.strokeDasharray} rx={4} opacity={vis.opacity}
        />
        {changed && <rect x={x0 - 1} y={3} width={w + 2} height={rowH - 6} fill="none" stroke={C.accent} strokeWidth={2} rx={5} opacity={0.9} />}
        {isSel && dragOk && <rect x={x0 - 1} y={3} width={w + 2} height={rowH - 6} fill="none" stroke={C.accent} strokeWidth={2} rx={5} opacity={0.9} />}
        {/* PR-000058 — the BOOKING TYPE bar, along the top edge. Inset 1px so it sits inside the
            chip border and its corners do not poke out of the rx=4 rounding. Drawn BEFORE the
            status stripe, the boundary caps and the drag handles: SVG paints in document order,
            so each of those lands on top of it and none of the three collisions occur.
            It renders on EVERY chip including the special states — the type is true regardless
            of status. On the solid maintenance fill it is the same colour as the body and so is
            invisible, which is correct and needs no special case. */}
        {topBarColor && w > 6 && (
          <rect x={x0 + 1} y={5} width={Math.max(0, w - 2)} height={TOP_BAR_H - 1} fill={topBarColor} rx={1.5} />
        )}
        {statusStripe && <rect x={x0} y={4} width={4} height={rowH - 8} fill={statusStripe} rx={1} />}
        {span.contPrev && boundaryCap('prev', x0, w, rowH, new Date(b.block_start_at!).getTime(), displayTz)}
        {span.contNext && boundaryCap('next', x0, w, rowH, bookingEndMs(b), displayTz)}
        {w > 46 && (() => {
          const textX = x0 + (statusStripe ? 8 : 6);
          // ⚠ ROW THREE IS THE TASK, NEVER THE PERSON (Ben, 2026-09-06). It uses taskRowLabel,
          // NOT bookingLabel: bookingLabel falls back to the PERSON when there is no title, which
          // on an untitled booking put the name on row two AND row three. taskRowLabel drops that
          // branch. bookingLabel itself is unchanged — BookingDetail still wants the composed form.
          //
          // ⚠ PR-000058 — THE SEPARATE 70-PIXEL LABEL GATE IS GONE, DELIBERATELY. A one hour chip
          // is SLOT_W = 50 pixels, so under the old pair of thresholds it rendered the time and
          // NOTHING ELSE — the single most common booking on the board showed no person and no
          // task. One threshold (the `w > 46` on this block) now does the whole job; below 46
          // nothing renders anyway. The `allowSuffix: w > 170` gate below is a DIFFERENT
          // judgement — whether the activity suffix fits on row three — and it stays.
          const taskLabel = truncateToWidth(
            taskRowLabel(b, kindsById, activitiesById),
            w, TEXT_LEFT_PAD, TASK_PX_PER_CHAR,
          );
          // Row one, to the right of the time: the meaningful status word. It MOVED here from row
          // two (D1: the generic "draft" text is dropped; unavailable shows none). The time is
          // four characters on a row of its own, so row one had spare width — and moving the
          // status off row two is what frees that row for the name.
          const statusWord = localPending ? (b._pending_op?.kind === 'cancel' ? '⌛ cancel queued' : '⌛ provisional')
            : conflicted ? '⚠ conflict'
            : b.series_assignee_conflict ? '⚠ assignee clash'
            : maint ? '🔧 maintenance'
            : unavail ? ''
            // ⚠ NO "notified" WORD, DELIBERATELY (Ben, 2026-09-17). This used to read
            // `b.student_notified ? '✓ notified' : ''`. Until booking notification emails exist,
            // student_notified records an APPROVAL OUTCOME, not that anybody was told anything, so
            // the board was asserting a phone call nobody had made. What replaces it is the amber
            // phone badge below: a warning only, never a reassurance.
            : pending ? '⏳ pending' : '';
          // Row two: the person. Staff see the STUDENT side; `counterpartyName` takes which side
          // to show so the student availability view can later ask for the other one without a
          // viewer prop on this component (every viewer of /ops-board today is staff).
          //
          // ⚠ NARROW-CHIP FALLBACK IS A CHAIN, NOT A TRUNCATION. "Smith, Fr…" reads as a fault;
          // "Smith" is a name. Try the full format, then the surname alone, and only then ellipsis.
          const personFull = counterpartyName(b, 'student');
          const surname = personSurname(leadParticipant(b, 'student')?.user ?? null);
          const nameBudget = Math.max(0, Math.floor((w - TEXT_LEFT_PAD) / NAME_PX_PER_CHAR));
          const personRow = !personFull ? ''
            : personFull.length <= nameBudget ? personFull
            : (surname && surname.length <= nameBudget) ? surname
            : truncateToWidth(personFull, w, TEXT_LEFT_PAD, NAME_PX_PER_CHAR);
          const bodyText = unavail ? '#64748b' : maint && kindColor ? '#fff' : C.text;
          const dimText = unavail ? '#64748b' : maint && kindColor ? 'rgba(255,255,255,0.85)' : C.textDim;
          return (
            <>
              <text x={textX} y={ROW1_Y} fill={headColor} fontSize={9} fontWeight={700} fontFamily={F}>
                {span.contPrev ? '··· ' : ''}{fmtTimeInTz(b.block_start_at!, displayTz)}
              </text>
              {statusWord && <text x={textX + 34} y={ROW1_Y} fill={localPending ? '#7c3aed' : conflicted || b.series_assignee_conflict ? C.red : maint && kindColor ? 'rgba(255,255,255,0.9)' : C.textDim} fontSize={8} fontFamily={M}>{statusWord}</text>}
              {personRow && <text x={textX} y={ROW2_Y} fill={bodyText} fontSize={NAME_PX} fontWeight={600} fontFamily={F}>{personRow}</text>}
              {taskLabel && <text x={textX} y={ROW3_Y} fill={dimText} fontSize={TASK_PX} fontFamily={F}>{taskLabel}</text>}
            </>
          );
        })()}
        {/* ── The amber phone badge: "this client needs a call" (Ben, 2026-09-17) ───────────────
            The ONLY notification signal left on the chip. It replaces the "✓ notified" word, which
            asserted something the system could not know. One symbol, two reasons — notifyWarning()
            decides which, and the panel reads the SAME function so the two cannot disagree.

            ⚠ DELIBERATELY OUTSIDE THE `w > 46` TEXT BLOCK. A warning must never be dropped for
            want of room: a chip too narrow for the text still gets the badge, at reduced size. A
            missing badge reads as "nothing to do here", which is the one thing it must never mean.

            Not drawn on maintenance or unavailable chips — neither has a client to ring. */}
        {!maint && !unavail && (() => {
          const warn = notifyWarning(b);
          if (!warn) return null;
          const r = w < 26 ? 3.5 : 5;
          const cx = x0 + w - r - 3;
          const cy = ROW1_Y - 4;
          return (
            <g pointerEvents="none">
              <title>{notifyWarningText(warn)}</title>
              <circle cx={cx} cy={cy} r={r} fill={C.amberLight} stroke={C.amber} strokeWidth={1} />
              <text x={cx} y={cy + r - 1} textAnchor="middle" fill={C.amber} fontSize={r * 1.6} fontFamily={F}>☎</text>
            </g>
          );
        })()}
        {/* PR-000031: fat edge handles on the selected draggable chip. Left only where the TRUE
            start renders (no continued-from-prev), right only where the TRUE finish renders —
            middle spans of a multi-day booking get neither. */}
        {isSel && dragOk && !span.contPrev && handleEl(x0, 'start', ob, displayTz, rowH)}
        {isSel && dragOk && !span.contNext && handleEl(x0 + w, 'finish', ob, displayTz, rowH)}
        {/* Live snapped-time readout at the dragged edge. */}
        {drag && drag.booking.id === ob.id && (() => {
          const ex = drag.edge === 'start' ? x0 : x0 + w;
          return (
            <text
              x={clamp(ex, 20, totalW - 20)} y={rowH - 4} textAnchor="middle" pointerEvents="none"
              fill={C.accent} stroke="#fff" strokeWidth={3} paintOrder="stroke"
              fontSize={11} fontWeight={700} fontFamily={M}
            >
              {fmtTimeInTz(new Date(drag.ghostMs).toISOString(), displayTz)}
            </text>
          );
        })()}
      </g>
    );
  };

  // External-aircraft chip — one per external allocation on its per-rego "External" row. Unlike
  // fleet rows (the row IS the resource), the rego rides on the box itself via
  // externalAircraftLabel. Same-rego chips can visually overlap WITHIN the row (render-only) —
  // but the BOOKING is clash-checked server-side: external aircraft get the PR-000023 same-rego
  // soft challenge (findExternalRegoOverlaps → 409 external_overlap, ack-required, deliberately
  // not a §B hard-block), and any fleet resources/crew on the booking pass §B as usual.
  // Stage 2: these chips share the ordinary chip's selection + edge-drag machinery — dragging any
  // of a booking's chips moves the WHOLE booking, so the external allocation rides along; a drag
  // that lands on a same-rego overlap is rejected server-side and snaps back (the "book anyway"
  // ack stays form-only for now). A booking with several external allocations shows handles on
  // each of its chips — all drive the same booking (accepted v1 wrinkle).
  const externalRect = (ob: Booking, alloc: Booking['asset_allocations'][number], rowH: number, displayTz: string) => {
    // Ghost override — identical to bookingRect, so every chip of a dragged booking moves together.
    const b: Booking = ghost && ghost.bookingId === ob.id
      ? { ...ob, block_start_at: new Date(ghost.startMs).toISOString(), arr_time_planned: new Date(ghost.endMs).toISOString() }
      : ob;
    const span = spanForDay(b, displayTz);
    if (!span) return null;
    const x0 = clamp(t2x(span.startH), 0, totalW);
    const x1 = clamp(t2x(span.startH + span.durH), 0, totalW);
    const w = Math.max(6, x1 - x0);
    const pending = isPending(b.approval_status);
    const declined = b.approval_status === 'declined';
    const cancelled = b.booking_status === 'cancelled';
    const localPending = !!b._pending_op;
    const conflicted = !localPending && b.sync_state === 'conflicted';
    const changed = changedIds.has(b.id);
    const kind = b.booking_kind_id ? kindsById.get(b.booking_kind_id) : undefined;
    const kindColor = kind?.ui_color;
    const unavail = !!kind?.is_unavailability;
    // ⚠ PR-000058/059 — THIS RENDERER GETS THE SAME SCHEME AS bookingRect, AND IT HAS TO.
    // The kickoff names only bookingRect, but the external-aircraft chip sits on the SAME BOARD
    // and drew its fill from the booking TYPE, exactly as the fleet chip used to. Changing one
    // and not the other would leave two chips a few rows apart using the same visual channel for
    // two different meanings — an operator reading fill-as-instructor would misread every
    // external chip. That defeats the scheme rather than merely looking inconsistent, so the
    // fill and the top bar are applied here too. The rego stays the label.
    // ⚠ THE SECOND CHIP RENDERER. It reads the SAME resolved pair — a settings change that moved
    // one renderer and not the other would put two chips on one board using the fill for two
    // different meanings, which is the failure the shared channel scheme exists to prevent.
    // Same explicit-final-arm rule as bookingRect above.
    const extChannelColor = (src: ChipChannelSource): string | null =>
      src === 'kind' ? (kindColor ?? null)
      : src === 'instructor' ? chipInstructorColor(b, staffById)
      : src === 'base' ? (basesById.get(b.base_id)?.ui_color ?? null)
      : null;
    const extFill = extChannelColor(chipChannels.fill);
    const extTopBar = extChannelColor(chipChannels.topBar);
    let bg: string, border: string;
    let statusStripe: string | null = null;
    if (localPending) { bg = '#f5f3ff'; border = '#7c3aed'; }
    else if (conflicted) { bg = '#fef2f2'; border = C.red; }
    else if (cancelled) { bg = '#f1f5f9'; border = C.red; }
    else if (unavail) { bg = `${UNAVAIL_COLOR}22`; border = UNAVAIL_COLOR; }
    else {
      bg = extFill ? `${extFill}${FILL_ALPHA}` : C.pill;
      border = extFill ?? C.textMuted;
      statusStripe = (pending || b.outside_hours) ? C.amber : declined ? C.red : b.booking_status === 'confirmed' ? C.green : C.textMuted;
    }
    const timeColor = localPending ? '#7c3aed' : conflicted ? C.red : unavail ? '#64748b' : pending ? C.amber : declined ? C.red : C.green;
    const isSel = selectedId === ob.id;
    const dragOk = canDragBooking(ob);
    return (
      <g key={`ext-${ob.id}-${alloc.id}`} onClick={(e) => onChipClick(e, ob)} style={{ cursor: 'pointer' }}>
        <rect
          x={x0} y={4} width={w} height={rowH - 8}
          fill={bg} stroke={border} strokeWidth={changed ? 2.5 : localPending || conflicted || pending ? 1.5 : 1}
          strokeDasharray={localPending || conflicted ? '3,3' : cancelled ? '4,2' : pending ? '5,3' : 'none'} rx={4}
          opacity={cancelled || declined ? 0.55 : 1}
        />
        {isSel && dragOk && <rect x={x0 - 1} y={3} width={w + 2} height={rowH - 6} fill="none" stroke={C.accent} strokeWidth={2} rx={5} opacity={0.9} />}
        {/* PR-000058 — the booking-type top bar, same geometry and same draw order as the
            fleet chip: before the stripe, so the stripe paints over it. */}
        {extTopBar && w > 6 && (
          <rect x={x0 + 1} y={5} width={Math.max(0, w - 2)} height={TOP_BAR_H - 1} fill={extTopBar} rx={1.5} />
        )}
        {statusStripe && <rect x={x0} y={4} width={4} height={rowH - 8} fill={statusStripe} rx={1} />}
        {span.contPrev && boundaryCap('prev', x0, w, rowH, new Date(b.block_start_at!).getTime(), displayTz)}
        {span.contNext && boundaryCap('next', x0, w, rowH, bookingEndMs(b), displayTz)}
        {w > 46 && (
          <>
            <text x={x0 + 6} y={20} fill={timeColor} fontSize={9} fontWeight={700} fontFamily={F}>
              {span.contPrev ? '··· ' : ''}{fmtTimeInTz(b.block_start_at!, displayTz)}
            </text>
            <text x={x0 + 6} y={34} fill={C.textDim} fontSize={8} fontFamily={M}>{externalAircraftLabel(alloc)}</text>
          </>
        )}
        {/* Shared drag affordances — same rules as bookingRect: handles only on the selected,
            draggable chip, and only where the TRUE start/finish renders (never on a continuing
            cross-midnight side). Callbacks keep the ORIGINAL row. */}
        {isSel && dragOk && !span.contPrev && handleEl(x0, 'start', ob, displayTz, rowH)}
        {isSel && dragOk && !span.contNext && handleEl(x0 + w, 'finish', ob, displayTz, rowH)}
        {drag && drag.booking.id === ob.id && (() => {
          const ex = drag.edge === 'start' ? x0 : x0 + w;
          return (
            <text
              x={clamp(ex, 20, totalW - 20)} y={rowH - 4} textAnchor="middle" pointerEvents="none"
              fill={C.accent} stroke="#fff" strokeWidth={3} paintOrder="stroke"
              fontSize={11} fontWeight={700} fontFamily={M}
            >
              {fmtTimeInTz(new Date(drag.ghostMs).toISOString(), displayTz)}
            </text>
          );
        })()}
      </g>
    );
  };

  // Greyed mirror block: resource is booked at ANOTHER base — show on its home row.
  const mirrorRect = (b: Booking, rowH: number, displayTz: string, otherBaseName: string) => {
    const span = spanForDay(b, displayTz);
    if (!span) return null;
    const x0 = clamp(t2x(span.startH), 0, totalW);
    const x1 = clamp(t2x(span.startH + span.durH), 0, totalW);
    const w = Math.max(6, x1 - x0);
    return (
      <g key={`mirror-${b.id}`} onClick={(e) => { e.stopPropagation(); setSelectedId(null); onBookingClick(b); }} style={{ cursor: 'pointer' }}>
        <rect x={x0} y={4} width={w} height={rowH - 8} fill="#f1f5f9" stroke={C.borderMed} strokeWidth={1} strokeDasharray="3,3" rx={4} opacity={0.9} />
        {w > 46 && (
          <text x={x0 + 6} y={22} fill={C.textMuted} fontSize={8} fontWeight={700} fontFamily={F}>
            {fmtTimeInTz(b.block_start_at!, displayTz)} · {otherBaseName} booking
          </text>
        )}
      </g>
    );
  };

  // Waitlist block (Step 5c.3 §B) — amber, dashed, at the requested time; clickable
  // (the detail panel carries the per-policy "Allocate" action).
  const waitlistRect = (b: Booking, rowH: number, displayTz: string) => {
    const span = spanForDay(b, displayTz);
    if (!span) return null;
    const x0 = clamp(t2x(span.startH), 0, totalW);
    const x1 = clamp(t2x(span.startH + span.durH), 0, totalW);
    const w = Math.max(6, x1 - x0);
    const changed = changedIds.has(b.id);
    return (
      <g key={`wl-${b.id}`} onClick={(e) => { e.stopPropagation(); setSelectedId(null); onBookingClick(b); }} style={{ cursor: 'pointer' }}>
        <rect x={x0} y={4} width={w} height={rowH - 8} fill={C.amberLight} stroke={C.amber} strokeWidth={changed ? 2.5 : 1.5} strokeDasharray="4,2" rx={4} />
        {w > 46 && (
          <text x={x0 + 6} y={22} fill={C.amber} fontSize={9} fontWeight={700} fontFamily={F}>
            ⏳ {fmtTimeInTz(b.block_start_at!, displayTz)}
          </text>
        )}
        {/* WHY it is on the waitlist. Displaced-by-maintenance and declined-then-released are both
            "unallocated", but only one of them means somebody said no (kickoff §5). */}
        {w > 70 && (
          <text x={x0 + 6} y={36} fill={C.textDim} fontSize={8} fontFamily={M}>
            {b.approval_status === 'declined' ? 'declined' : 'unallocated'}
          </text>
        )}
        {/* ── The same amber phone badge the occupying chips carry (Ben, 2026-09-18) ──────────────
            A maintenance displacement waitlists the booking AND sets the pen, so this lane holds
            the case where a client most needs a call. Before this the board showed nothing here
            while the detail panel showed the amber line, and the two disagreed.

            Same helper, same symbol, same <title> text as the chip badge in bookingRect above —
            that is the whole point of notifyWarning() being shared. Drawn OUTSIDE the `w > 46`
            and `w > 70` text guards for the same reason as the chip's: a warning must never be
            dropped for want of room, because a missing badge reads as "nothing to do here".

            The waitlist row is its own <g> with a plain rect, so it carries this at no structural
            cost — no layout change, no new geometry, just the badge at the rect's top-right. */}
        {(() => {
          const warn = notifyWarning(b);
          if (!warn) return null;
          const r = w < 26 ? 3.5 : 5;
          const cx = x0 + w - r - 3;
          const cy = 4 + r + 3;
          return (
            <g pointerEvents="none">
              <title>{notifyWarningText(warn)}</title>
              <circle cx={cx} cy={cy} r={r} fill={C.amberLight} stroke={C.amber} strokeWidth={1} />
              <text x={cx} y={cy + r - 1} textAnchor="middle" fill={C.amber} fontSize={r * 1.6} fontFamily={F}>☎</text>
            </g>
          );
        })()}
      </g>
    );
  };

  // Recurring-series occurrence that is still UNASSIGNED (Phase 2) — indigo, dashed, at its slot
  // time; 🔁 marker. Clickable → open it → add crew + aircraft → Save (a normal PUT). Distinct from
  // the waitlist (a standing task that needs allocation, not a booking holding a contested slot).
  const unassignedRect = (b: Booking, rowH: number, displayTz: string) => {
    const span = spanForDay(b, displayTz);
    if (!span) return null;
    const x0 = clamp(t2x(span.startH), 0, totalW);
    const x1 = clamp(t2x(span.startH + span.durH), 0, totalW);
    const w = Math.max(6, x1 - x0);
    const changed = changedIds.has(b.id);
    return (
      <g key={`un-${b.id}`} onClick={(e) => { e.stopPropagation(); setSelectedId(null); onBookingClick(b); }} style={{ cursor: 'pointer' }}>
        <rect x={x0} y={4} width={w} height={rowH - 8} fill="#eef2ff" stroke="#6366f1" strokeWidth={changed ? 2.5 : 1.5} strokeDasharray="4,2" rx={4} />
        {w > 46 && (
          <text x={x0 + 6} y={22} fill="#4f46e5" fontSize={9} fontWeight={700} fontFamily={F}>
            🔁 {fmtTimeInTz(b.block_start_at!, displayTz)}
          </text>
        )}
        {w > 80 && (
          <text x={x0 + 6} y={36} fill={C.textDim} fontSize={8} fontFamily={M}>Unassigned — recurring</text>
        )}
      </g>
    );
  };

  // Post-ECD maintenance "tail" — the aircraft is U/S past its planned completion (ECD) and not yet
  // confirmed back. Derived from the AIRCRAFT state (Fix 2) so the "confirm availability" warning
  // PERSISTS for the whole overdue period, regardless of the booking fetch window. Shown strictly
  // AFTER the ECD day — the firm window (commencement→ECD) is the solid booking chip, never this
  // banner. expected_return_date / unserviceable_from are @db.Date (date-granular; ≤1-day UTC
  // tolerance, same as before). UFN (no ECD) → no tail (open-ended chip). CLICKABLE when maintByReg
  // resolves the maintenance booking → opens confirm-return. Since listBookings now returns active
  // (un-returned, un-cancelled) maintenance tenant-wide regardless of the window (the far-overdue
  // carve-out — see design-notes-maintenance-farfetch-confirm-return.md), maintByReg resolves for
  // the WHOLE overdue period, so the tail stays clickable throughout. The non-clickable fallback
  // (mb == null) now only remains for legacy ad-hoc U/S that has no backing booking at all.
  // Legacy ad-hoc U/S (null unserviceable_from + no ECD) → no tail.
  const maintenanceTail = (ac: Aircraft, rowH: number) => {
    if (ac.serviceable !== false || !ac.expected_return_date) return null;
    const ecdDay = ac.expected_return_date.slice(0, 10);
    if (date < ecdDay) return null; // before the ECD day → the solid chip covers the firm window
    if (ac.unserviceable_from && date < ac.unserviceable_from.slice(0, 10)) return null; // defensive (before commencement)
    const mb = maintByReg.get(ac.registration); // resolves the maintenance booking (clickable + window end)
    const ecd = ddMMMyy(ac.expected_return_date);
    const label = mb
      ? `Maintenance — expected completion ${ecd} — click to confirm returned`
      : `Maintenance — expected completion ${ecd} — overdue, confirm availability`;

    // Geometry. STRICTLY AFTER the ECD day → full-row hatch (unchanged). ON the ECD day itself →
    // partial hatch from the maintenance window END (the due-back instant, from maintByReg's block
    // end in the section tz) to end-of-row — before that instant the solid chip covers the firm
    // window. If the booking isn't resolvable (degraded far-fetch fallback), fall back to the
    // pre-change rule (no tail on the ECD day) rather than guessing the window end.
    let x = 1;
    let w = totalW - 2;
    if (date === ecdDay) {
      if (!mb) return null;
      const { from } = zonedDayRangeUtc(date, sectionTz);
      const dueBackH = (bookingEndMs(mb) - new Date(from).getTime()) / 3_600_000; // due-back local hour in section tz
      x = clamp(t2x(dueBackH), 1, totalW - 1);
      w = totalW - 1 - x;
      if (w < 2) return null; // due-back at/after the visible track end → the solid chip covers the day
    }
    const labelX = x <= 1 ? 10 : clamp(x + 8, 1, totalW - 4);
    // PR-000028: ALWAYS stopPropagation so the hatch can never fall through to the row's
    // create-a-booking handler; only open a booking when one is resolvable. When mb==null
    // (legacy strand / ad-hoc U/S with no booking) the click is swallowed, not misfired.
    return (
      <g onClick={(e) => { e.stopPropagation(); if (mb) onBookingClick(mb); }} style={{ cursor: mb ? 'pointer' : 'default' }}>
        <title>{mb ? label : `${label} (no linked maintenance booking — clear via Aircraft admin)`}</title>
        <rect x={x} y={4} width={w} height={rowH - 8} fill="url(#overdueHatch)" stroke={C.amber} strokeWidth={1} strokeDasharray="6,3" rx={4} opacity={0.9} />
        <text x={labelX} y={Math.round(rowH / 2) + 3} fill={C.amber} fontSize={10} fontWeight={700} fontFamily={F}>{label}</text>
      </g>
    );
  };

  // ── Cells ──────────────────────────────────────────────────────────────────

  const labelCell = (title: string, subtitle: string, accent: boolean, badge?: string, titleAttr?: string, relocateReg?: string) => (
    <div
      title={titleAttr}
      style={{
        width: LABEL_W, flexShrink: 0, marginRight: 4, background: C.surface,
        border: `1px solid ${accent ? C.amberBorder : C.border}`, borderRadius: 6, padding: '5px 8px',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.text, fontFamily: M }}>{title}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {relocateReg && canWorkflow && (
            <button
              title="Relocate aircraft…"
              onClick={(e) => { e.stopPropagation(); onRelocate(relocateReg); }}
              style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 4, color: C.textDim, cursor: 'pointer', fontSize: 10, lineHeight: 1, padding: '1px 4px', fontFamily: F }}
            >⇄</button>
          )}
          {badge && <span style={{ fontSize: 8, color: C.textMuted }}>{badge}</span>}
        </span>
      </div>
      <div style={{ fontSize: 9, color: C.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{subtitle}</div>
    </div>
  );

  const sectionHeader = (label: string) => (
    <div style={{ display: 'flex', marginTop: 8, marginBottom: 4 }}>
      {/* PR-000050: same spacer footprint as labelCell (LABEL_W + 4px marginRight) so the section
          label starts where the row tracks start. */}
      <div style={{ width: LABEL_W, flexShrink: 0, marginRight: 4 }} />
      <div style={{ flex: 1, fontSize: 9, fontWeight: 700, color: C.textMuted, letterSpacing: '0.08em', padding: '2px 4px' }}>{label}</div>
    </div>
  );

  const gridLines = (h: number) => hours.map((hr) => <line key={hr} x1={t2x(hr)} y1={0} x2={t2x(hr)} y2={h} stroke={C.border} strokeWidth={0.5} />);

  const outsideShade = (rowH: number) => {
    if (!base?.operating_hours_start || !base?.operating_hours_end) return null;
    const open = parseInt(base.operating_hours_start.slice(0, 2), 10) + parseInt(base.operating_hours_start.slice(3, 5), 10) / 60;
    const close = parseInt(base.operating_hours_end.slice(0, 2), 10) + parseInt(base.operating_hours_end.slice(3, 5), 10) / 60;
    return (
      <>
        {open > START && <rect x={0} y={0} width={t2x(open)} height={rowH} fill="#f8fafc" />}
        {close < START + TOTAL && <rect x={t2x(close)} y={0} width={totalW - t2x(close)} height={rowH} fill="#f8fafc" />}
      </>
    );
  };

  // ── Resource selection for this section ──────────────────────────────────────

  let sectionAircraft: Aircraft[]; // resident at this base (base mode) or off-base (off mode)
  let visitingAircraft: Aircraft[] = [];
  if (offBase) {
    sectionAircraft = aircraft.filter((a) => aircraftResidentBaseIcao(a) === null);
    if (!allBases) sectionAircraft = sectionAircraft.filter((a) => rowPool.some((b) => allocHasAircraft(b, a.registration)));
  } else {
    sectionAircraft = aircraft.filter((a) => aircraftResidentBaseIcao(a) === base!.icao);
    visitingAircraft = aircraft.filter((a) => {
      const home = aircraftResidentBaseIcao(a);
      return home !== null && home !== base!.icao && rowPool.some((b) => b.base_id === base!.id && allocHasAircraft(b, a.registration));
    });
  }

  const residentAssets = offBase ? [] : assets.filter((a) => a.base_id === base!.id);
  const visitingAssets = offBase ? [] : assets.filter((a) => a.base_id !== base!.id && rowPool.some((b) => b.base_id === base!.id && allocHasAsset(b, a.id)));
  const residentStaff = offBase ? [] : staff.filter((s) => instructorBaseId(s) === base!.id);
  const visitingStaff = offBase ? [] : staff.filter((s) => instructorBaseId(s) !== base!.id && rowPool.some((b) => b.base_id === base!.id && isStaffOn(b, s.id)));

  // External aircraft (Phase 1) — a chip per external allocation that falls on this day at this
  // base. Row-internal overlaps are visual-only (the board draws them as-is), but external
  // bookings ARE clash-checked server-side via the PR-000023 same-rego soft challenge — see
  // externalRect. Rows only appear when something is on them (no empty rows).
  const externalPairs = offBase ? [] : rowPool
    .filter((b) => b.base_id === base!.id)
    .flatMap((b) => b.asset_allocations.filter((a) => a.external_aircraft_source).map((a) => ({ b, a })))
    .filter(({ b }) => spanForDay(b, sectionTz) != null);

  // PR-000023 Phase 5 — one sub-row per distinct external rego (instead of one flat row for all).
  // Group by a normalised key (trim + upper-case; the RAW string of the first occurrence is shown),
  // sorted alphabetically. Same-rego same-time chips can still overlap WITHIN their row — accepted
  // for v1; the creation-time external-overlap warning is the guard. (WeekView is count-based and
  // untouched.) NOTE: this grouping is deliberately trim+case only, so `VH-SJW` and `VH SJW` land in
  // separate rows — looser than the create-time overlap check (which also strips spaces/hyphens).
  type ExternalPair = (typeof externalPairs)[number];
  const externalRegoGroups: { rego: string; sources: Set<string>; pairs: ExternalPair[] }[] = (() => {
    const map = new Map<string, { rego: string; sources: Set<string>; pairs: ExternalPair[] }>();
    for (const pair of externalPairs) {
      const raw = (pair.a.external_aircraft_registration ?? '').trim();
      const key = raw.toUpperCase();
      let g = map.get(key);
      if (!g) { g = { rego: raw || '—', sources: new Set<string>(), pairs: [] }; map.set(key, g); }
      if (pair.a.external_aircraft_source) g.sources.add(pair.a.external_aircraft_source);
      g.pairs.push(pair);
    }
    return [...map.values()].sort((x, y) => x.rego.toUpperCase().localeCompare(y.rego.toUpperCase()));
  })();
  const externalSourceLabel = (sources: Set<string>): string =>
    sources.size > 1 ? 'Cross-hire / client' : sources.has('crosshire') ? 'Crosshire' : 'Client';

  if (offBase && sectionAircraft.length === 0) return null;

  // Resident resource: split its bookings into at-base (normal) vs away (mirror).
  const residentBlocks = (rowBookings: Booking[]) =>
    rowBookings.map((b) => {
      if (!offBase && base && b.base_id !== base.id) {
        return mirrorRect(b, ROW_H, sectionTz, baseById.get(b.base_id)?.name ?? 'Other base');
      }
      return bookingRect(b, ROW_H, offBase ? b.dep_timezone : sectionTz);
    });

  // ── Aircraft row ─────────────────────────────────────────────────────────────
  const aircraftRow = (ac: Aircraft, visiting: boolean) => {
    const needsApproval = ac.approval_required; // flag-authoritative (5c.2.1)
    const degraded = !!ac.capability_current && ac.capability_current !== ac.capability_normal;
    const us = ac.serviceable === false;
    const homeIcaoName = ac.current_base_icao ? (baseByIcao.get(ac.current_base_icao)?.name ?? ac.current_base_icao) : 'off-base';
    let subtitle: string;
    if (visiting) subtitle = `visiting — ${homeIcaoName}${degraded ? ` · ⚠ ${ac.capability_current}` : ''}`;
    else if (degraded) subtitle = `${ac.model ?? ac.aircraft_category} · ⚠ ${ac.capability_current}`;
    else if (offBase) subtitle = `${ac.model ?? ac.aircraft_category}`;
    else subtitle = `${ac.model ?? ac.aircraft_category}`;
    const badge = visiting ? '✈' : offBase ? (ac.current_base_icao ?? 'OFF') : (ac.current_base_icao ?? undefined);
    const rowBookings = visiting
      ? rowPool.filter((b) => b.base_id === base!.id && allocHasAircraft(b, ac.registration))
      : rowPool.filter((b) => allocHasAircraft(b, ac.registration));
    return (
      <div key={`${visiting ? 'v-' : ''}${ac.registration}`} id={visiting ? undefined : `ops-ac-${ac.registration}`} style={{ display: 'flex', marginBottom: 5 }}>
        {labelCell(ac.registration, subtitle, needsApproval || degraded || us, badge, ac.status_note ?? undefined, ac.registration)}
        <div
          style={{ flex: 1, background: visiting ? C.surfaceAlt : C.surface, border: `1px solid ${us ? C.accentBorder : needsApproval ? C.amberBorder : C.border}`, borderRadius: 6, overflow: 'hidden', cursor: 'pointer', position: 'relative' }}
          onClick={(e) => trackClick({ baseId: base?.id ?? '', aircraftReg: ac.registration, startTime: slotTimeFromClick(e) })}
        >
          <svg width={totalW} height={ROW_H} style={{ display: 'block' }}>
            {outsideShade(ROW_H)}
            {gridLines(ROW_H)}
            {!visiting && maintenanceTail(ac, ROW_H)}
            {visiting ? rowBookings.map((b) => bookingRect(b, ROW_H, sectionTz)) : residentBlocks(rowBookings)}
          </svg>
        </div>
      </div>
    );
  };

  return (
    <div style={{ marginBottom: allBases ? 18 : 0 }}>
      <svg width={0} height={0} style={{ position: 'absolute' }} aria-hidden>
        <defs>
          <pattern id="bufHatch" width={6} height={6} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width={6} height={6} fill={C.amberLight} />
            <line x1={0} y1={0} x2={0} y2={6} stroke={C.amberBorder} strokeWidth={2} />
          </pattern>
          <pattern id="maintHatch" width={8} height={8} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width={8} height={8} fill={C.accentLight} />
            <line x1={0} y1={0} x2={0} y2={8} stroke={C.accentBorder} strokeWidth={2} />
          </pattern>
          <pattern id="overdueHatch" width={8} height={8} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width={8} height={8} fill={C.amberLight} />
            <line x1={0} y1={0} x2={0} y2={8} stroke={C.amberBorder} strokeWidth={2} />
          </pattern>
          {/* Day-boundary cap (Stage 2) — no background rect, so the chip's own fill shows
              through between the slate stripes. */}
          <pattern id="dayBoundaryHatch" width={4} height={4} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1={0} y1={0} x2={0} y2={4} stroke="#64748b" strokeWidth={1.5} />
          </pattern>
        </defs>
      </svg>

      {showHeader && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0 2px' }}>
          <span style={{ background: base?.ui_color ?? C.slate, color: '#fff', borderRadius: 5, padding: '3px 10px', fontSize: 12, fontWeight: 700, fontFamily: F }}>
            {offBase ? 'Off-base / Other' : base!.name}
          </span>
          {!offBase && base?.icao && <span style={{ fontSize: 10, color: C.textMuted, fontFamily: M }}>{base.icao}</span>}
        </div>
      )}

      {/* Mobile-only hint — the schedule below scrolls horizontally to reach the full day. */}
      {isMobile && (
        <div style={{ fontSize: 11, color: C.textMuted, padding: '2px 0 4px', fontFamily: F }}>
          ↔ Drag the schedule to see the full day
        </div>
      )}

      {/* The time-axis header AND every resource row share ONE horizontal scroll — now provided by
          the board column (OpsBoard BODY, overflow:auto) rather than this wrapper, so the axis can
          stick VERTICALLY against that scrollport. This wrapper stays overflow:visible; the inner
          minWidth track keeps the flex:1 timeline divs from collapsing (the June mobile fix) and
          the track stays totalW wide so slot-click geometry is unchanged. Axis + rows move together
          horizontally because they live in the same scroll container. */}
      <div style={{ overflow: 'visible' }}>
      {/* PR-000050: minWidth includes the label cell's 4px margin so it matches the real row width. */}
      <div style={{ minWidth: LABEL_W + 4 + totalW }}>

      {/* Time axis — sticky to the top of the board scrollport so it stays visible while the board
          scrolls. z-index above the booking chips (position:relative, no z-index); opaque board
          background so chips passing under it are covered.
          PR-000050 (Chris): the axis must sit in the IDENTICAL horizontal box as a row track, or
          its ticks drift left of the body gridlines. A row is labelCell (LABEL_W + 4px marginRight)
          then a flex:1 track with a 1px border, so the row SVG's x=0 sits at LABEL_W + 5. The
          spacer below mirrors labelCell's footprint and the wrapper mirrors the track's border
          contribution (transparent, so nothing is drawn) — reproduced structurally, not hand-tuned,
          so the two cannot drift apart again. */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 2, position: 'sticky', top: 0, zIndex: 5, background: C.bg, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ width: LABEL_W, flexShrink: 0, marginRight: 4 }} />
        <div style={{ flex: 1, border: '1px solid transparent' }}>
          <svg width={totalW} height={22} style={{ display: 'block' }}>
            {hours.map((h) => (
              <g key={h}>
                <line x1={t2x(h)} y1={0} x2={t2x(h)} y2={18} stroke={C.border} strokeWidth={1} />
                <text x={t2x(h) + 2} y={14} fill={C.textMuted} fontSize={9} fontFamily={M}>{String(h).padStart(2, '0')}:00</text>
              </g>
            ))}
          </svg>
        </div>
      </div>

      {/* Waitlist line (Step 5c.3 §B) — highlighted, at the top of each base section. */}
      {!offBase && (() => {
        const sectionWaitlist = waitlist.filter((b) => b.base_id === base!.id);
        if (sectionWaitlist.length === 0) return null;
        return (
          <div style={{ display: 'flex', marginBottom: 5, marginTop: 4 }}>
            {labelCell('⏳ Waitlist', `${sectionWaitlist.length} unallocated`, true)}
            <div style={{ flex: 1, background: '#fffbeb', border: `1px solid ${C.amberBorder}`, borderRadius: 6, overflow: 'hidden', position: 'relative' }}>
              <svg width={totalW} height={ROW_H} style={{ display: 'block' }}>
                {gridLines(ROW_H)}
                {sectionWaitlist.map((b) => waitlistRect(b, ROW_H, sectionTz))}
              </svg>
            </div>
          </div>
        );
      })()}

      {/* Unassigned — needs allocation (Recurring Phase 2). A dedicated per-base lane for confirmed
          series occurrences with no crew/allocation, semantically distinct from the Waitlist. Open
          one to assign crew + aircraft on the day (a normal PUT). */}
      {!offBase && (() => {
        const sectionUnassigned = unassigned.filter((b) => b.base_id === base!.id);
        if (sectionUnassigned.length === 0) return null;
        return (
          <div style={{ display: 'flex', marginBottom: 5, marginTop: 4 }}>
            {labelCell('🔁 Unassigned', `${sectionUnassigned.length} to allocate`, true)}
            <div style={{ flex: 1, background: '#f5f6ff', border: '1px solid #c7d2fe', borderRadius: 6, overflow: 'hidden', position: 'relative' }}>
              <svg width={totalW} height={ROW_H} style={{ display: 'block' }}>
                {gridLines(ROW_H)}
                {sectionUnassigned.map((b) => unassignedRect(b, ROW_H, sectionTz))}
              </svg>
            </div>
          </div>
        );
      })()}

      {filters.aircraft && (
        <>
          {sectionHeader(offBase ? 'OFF-BASE / OTHER AIRCRAFT' : 'AIRCRAFT')}
          {sectionAircraft.length === 0 && visitingAircraft.length === 0 && <div style={{ fontSize: 11, color: C.textMuted, padding: `4px 0 4px ${LABEL_W + 4}px` }}>No aircraft at this base.</div>}
          {sectionAircraft.map((ac) => aircraftRow(ac, false))}
          {visitingAircraft.map((ac) => aircraftRow(ac, true))}
        </>
      )}

      {!offBase && filters.assets && (residentAssets.length > 0 || visitingAssets.length > 0) && (
        <>
          {sectionHeader('ASSETS')}
          {residentAssets.map((as) => {
            const rowBookings = rowPool.filter((b) => allocHasAsset(b, as.id));
            return (
              <div key={as.id} style={{ display: 'flex', marginBottom: 5 }}>
                {labelCell(as.short_code ?? as.name, as.category ?? 'Asset', as.ownership !== 'school_owned' || as.approval_required)}
                <div
                  style={{ flex: 1, background: C.surface, border: `1px solid ${as.ownership !== 'school_owned' ? C.amberBorder : C.border}`, borderRadius: 6, overflow: 'hidden', position: 'relative', cursor: 'pointer' }}
                  onClick={(e) => trackClick({ baseId: base?.id ?? '', assetId: as.id, startTime: slotTimeFromClick(e) })}
                >
                  <svg width={totalW} height={ROW_H} style={{ display: 'block' }}>
                    {gridLines(ROW_H)}
                    {residentBlocks(rowBookings)}
                  </svg>
                </div>
              </div>
            );
          })}
          {visitingAssets.map((as) => {
            const homeName = baseById.get(as.base_id)?.name ?? 'elsewhere';
            const rowBookings = rowPool.filter((b) => b.base_id === base!.id && allocHasAsset(b, as.id));
            return (
              <div key={`v-${as.id}`} style={{ display: 'flex', marginBottom: 5 }}>
                {labelCell(as.short_code ?? as.name, `visiting — ${homeName}`, false, '✈')}
                <div
                  style={{ flex: 1, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 6, overflow: 'hidden', position: 'relative', cursor: 'pointer' }}
                  onClick={(e) => trackClick({ baseId: base?.id ?? '', assetId: as.id, startTime: slotTimeFromClick(e) })}
                >
                  <svg width={totalW} height={ROW_H} style={{ display: 'block' }}>
                    {gridLines(ROW_H)}
                    {rowBookings.map((b) => bookingRect(b, ROW_H, sectionTz))}
                  </svg>
                </div>
              </div>
            );
          })}
        </>
      )}

      {!offBase && filters.aircraft && externalRegoGroups.length > 0 && (
        <>
          {sectionHeader('EXTERNAL AIRCRAFT')}
          {externalRegoGroups.map((g) => (
            <div key={`extrow-${g.rego.toUpperCase()}`} style={{ display: 'flex', marginBottom: 5 }}>
              {labelCell(g.rego, externalSourceLabel(g.sources), false)}
              <div style={{ flex: 1, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, overflow: 'hidden', position: 'relative' }}>
                <svg width={totalW} height={ROW_H} style={{ display: 'block' }}>
                  {gridLines(ROW_H)}
                  {g.pairs.map(({ b, a }) => externalRect(b, a, ROW_H, sectionTz))}
                </svg>
              </div>
            </div>
          ))}
        </>
      )}

      {!offBase && filters.instructors && (residentStaff.length > 0 || visitingStaff.length > 0) && (
        <>
          {sectionHeader('INSTRUCTORS')}
          {residentStaff.map((s) => {
            const restricted = s.availability_mode !== 'always' || s.approval_required;
            const rowBookings = rowPool.filter((b) => isStaffOn(b, s.id));
            return (
              <div key={s.id} id={`ops-staff-${s.id}`} style={{ display: 'flex', marginBottom: 5 }}>
                {labelCell(s.pilot_code ?? s.preferred_name ?? s.full_name, restricted ? "Approval req'd" : 'Always available', restricted, s.role ?? undefined)}
                <div
                  style={{ flex: 1, background: restricted ? '#fffbeb' : C.surface, border: `1px solid ${restricted ? C.amberBorder : C.border}`, borderRadius: 6, overflow: 'hidden', position: 'relative', cursor: 'pointer' }}
                  onClick={(e) => trackClick({ baseId: base?.id ?? '', pilotId: s.id, startTime: slotTimeFromClick(e) })}
                >
                  <svg width={totalW} height={ROW_H} style={{ display: 'block' }}>
                    {gridLines(ROW_H)}
                    {residentBlocks(rowBookings)}
                  </svg>
                </div>
              </div>
            );
          })}
          {visitingStaff.map((s) => {
            const home = instructorBaseId(s);
            const homeName = (home && baseById.get(home)?.name) || 'elsewhere';
            const rowBookings = rowPool.filter((b) => b.base_id === base!.id && isStaffOn(b, s.id));
            return (
              <div key={`visit-${s.id}`} style={{ display: 'flex', marginBottom: 5 }}>
                {labelCell(s.pilot_code ?? s.preferred_name ?? s.full_name, `visiting — ${homeName}`, false, '✈')}
                <div
                  style={{ flex: 1, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 6, overflow: 'hidden', position: 'relative', cursor: 'pointer' }}
                  onClick={(e) => trackClick({ baseId: base?.id ?? '', pilotId: s.id, startTime: slotTimeFromClick(e) })}
                >
                  <svg width={totalW} height={ROW_H} style={{ display: 'block' }}>
                    {gridLines(ROW_H)}
                    {rowBookings.map((b) => bookingRect(b, ROW_H, sectionTz))}
                  </svg>
                </div>
              </div>
            );
          })}
        </>
      )}

      </div>
      </div>

      {/* PR-000031 — move-vs-resize confirm. The UNIVERSAL dialog for every edge-drag (single-
          and multi-day): both interpretations with concrete results + duration readout. Cancel
          (or a server rejection upstream) snaps the chip back — the ghost clears with the state. */}
      {confirmDrag && (() => {
        const cd = confirmDrag;
        const origEdgeMs = cd.edge === 'start' ? cd.origStartMs : cd.origEndMs;
        const deltaMins = Math.round((cd.newEdgeMs - origEdgeMs) / 60_000);
        const later = deltaMins > 0;
        const durMins = Math.round((cd.origEndMs - cd.origStartMs) / 60_000);
        const moveStartMs = cd.origStartMs + deltaMins * 60_000;
        const moveEndMs = cd.origEndMs + deltaMins * 60_000;
        const adjStartMs = cd.edge === 'start' ? cd.newEdgeMs : cd.origStartMs;
        const adjEndMs = cd.edge === 'finish' ? cd.newEdgeMs : cd.origEndMs;
        const adjDurMins = Math.round((adjEndMs - adjStartMs) / 60_000);
        // ⚠ bookingLabel HERE, deliberately, and NOT taskRowLabel. This is the drag-confirm
        // dialog: ONE line that has to identify which booking is being moved, with no separate
        // person field anywhere in it. The composed "person – activity" is the whole identifier,
        // so dropping the person would leave the operator confirming a reschedule against an
        // activity name alone. The duplication fixed on 2026-09-06 was specific to the surfaces
        // that list the person on their own line (chip row three, and the hover tiers).
        const label = bookingLabel(cd.booking, kindsById, activitiesById, { allowSuffix: true });
        const title = later
          ? `Delay this booking's ${cd.edge} by ${fmtDurMins(deltaMins)}?`
          : `Bring this booking's ${cd.edge} forward by ${fmtDurMins(-deltaMins)}?`;
        const commit = async (startMs: number, endMs: number) => {
          setDragSubmitting(true);
          await onReschedule(cd.booking, {
            block_start_at: new Date(startMs).toISOString(),
            arr_time_planned: new Date(endMs).toISOString(),
            activity_mins: Math.round((endMs - startMs) / 60_000),
          });
          // ok → the refreshed/overlaid data holds the new time; fail → clearing the ghost snaps back.
          setDragSubmitting(false);
          setConfirmDrag(null);
        };
        const optBtn = (heading: string, sub: string, onPick: () => void) => (
          <button
            onClick={onPick}
            disabled={dragSubmitting}
            style={{ display: 'block', width: '100%', textAlign: 'left', background: C.accentLight, border: `1px solid ${C.accentBorder}`, borderRadius: 8, padding: '10px 12px', marginBottom: 8, cursor: dragSubmitting ? 'wait' : 'pointer', fontFamily: F, opacity: dragSubmitting ? 0.6 : 1 }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: C.accent, marginBottom: 3 }}>{heading}</div>
            <div style={{ fontSize: 11, color: C.textDim, lineHeight: 1.4 }}>{sub}</div>
          </button>
        );
        return (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 320, padding: 16 }}
            onClick={() => { if (!dragSubmitting) setConfirmDrag(null); }}
          >
            <div onClick={(e) => e.stopPropagation()} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 22, width: '100%', maxWidth: 420, fontFamily: F }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 4 }}>{title}</div>
              <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 14 }}>
                {label} · currently {fmtEdgeInTz(cd.origStartMs, cd.tz)} – {fmtEdgeInTz(cd.origEndMs, cd.tz)}
              </div>
              {optBtn(
                'Move whole booking',
                `${fmtEdgeInTz(moveStartMs, cd.tz)} – ${fmtEdgeInTz(moveEndMs, cd.tz)} · duration unchanged (${fmtDurMins(durMins)})`,
                () => commit(moveStartMs, moveEndMs),
              )}
              {optBtn(
                cd.edge === 'start' ? 'Adjust start only' : 'Adjust finish only',
                cd.edge === 'start'
                  ? `Starts ${fmtEdgeInTz(adjStartMs, cd.tz)} · same finish ${fmtEdgeInTz(adjEndMs, cd.tz)} · duration now ${fmtDurMins(adjDurMins)}`
                  : `Finishes ${fmtEdgeInTz(adjEndMs, cd.tz)} · same start ${fmtEdgeInTz(adjStartMs, cd.tz)} · duration now ${fmtDurMins(adjDurMins)}`,
                () => commit(adjStartMs, adjEndMs),
              )}
              <button
                onClick={() => setConfirmDrag(null)}
                disabled={dragSubmitting}
                style={{ width: '100%', background: 'none', border: `1px solid ${C.border}`, color: C.textMuted, borderRadius: 8, padding: '9px', fontSize: 12, cursor: dragSubmitting ? 'wait' : 'pointer', fontFamily: F }}
              >Cancel</button>
            </div>
          </div>
        );
      })()}
      {/* PR-000058 D2 — TIER TWO HOVER CARD.
          ⚠ IT CANNOT LIVE INSIDE THE ROW. Every row track sets overflow:'hidden' (aircraft,
          assets, external and both instructor sections), so chips clip at the row edges: growing
          the chip on hover does not work, and neither does an absolutely positioned child inside
          the row. It is portalled to document.body and positioned from the chip's own bounding
          rectangle instead.
          ⚠ pointer-events:'none', ALWAYS. Chips are draggable (PR-000031, live in prod) and the
          row track underneath is clickable to create a booking in that slot. A card that ate
          pointer events would break both, silently.
          Suppressed outright while an interaction is live — using the LOCAL interactionActive,
          not the onDragActiveChange callback, which exists so the board can pause its poll tick. */}
      {canHover && hoverCard && !interactionActive && createPortal(
        <div
          style={{
            position: 'fixed',
            left: Math.min(hoverCard.x, window.innerWidth - 260),
            top: Math.min(hoverCard.y + 6, window.innerHeight - 120),
            zIndex: 9999,
            pointerEvents: 'none',
            maxWidth: 250,
            background: C.surface,
            border: `1px solid ${C.borderMed}`,
            borderRadius: 6,
            boxShadow: '0 6px 18px rgba(15,41,66,0.18)',
            padding: '7px 10px',
            fontFamily: F,
          }}
        >
          {hoverCard.lines.map((ln, i) => (
            <div
              key={i}
              style={{
                fontSize: i === 0 ? 11 : 10,
                fontWeight: i === 0 ? 700 : i === 1 ? 600 : 400,
                color: i === 0 ? C.text : i === 1 ? C.text : C.textDim,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {ln}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

// The board legend.
//
// ⚠ IT USED TO DESCRIBE THE PRE-0.12.3 SCHEME, and nobody noticed for a release (Ben spotted it
// on the live board, 2026-09-07). It was a hardcoded array of eight swatches written when the
// FILL carried the booking's status. The chip work moved status to the left bar, the type to a
// top bar and the instructor to the fill — and never touched this. Checked swatch by swatch
// against what bookingRect actually renders, three of the eight were wrong:
//
//   "Confirmed", green fill        → the fill is the INSTRUCTOR; confirmation is the LEFT BAR
//   "Pending approval", amber fill → the dashed border was right, the amber fill was not
//   "Student not yet notified", a dot
//                                  → RENDERED NOWHERE. At the time, `student_notified` appeared
//                                    once in this file and produced the WORDS "✓ notified" on row
//                                    one. There was never a dot.
//
// ⚠ AND THAT WORD IS NOW GONE TOO (Ben, 2026-09-17). `student_notified` is read NOWHERE in this
// file any more: until booking notification emails exist every student and client is ASSUMED
// notified, so a "✓ notified" label was the board asserting a phone call nobody had made. What the
// chip carries instead is the amber phone badge — a WARNING and never a reassurance — decided by
// lib/notifyWarning.ts, which the booking detail panel reads too so the two cannot disagree.
//
// ⚠ AND THE BIGGER GAP: it said nothing about the two channels at all. A legend that explains the
// states but not the colour scheme is worse than no legend, because a reader concludes the fill
// still means status — exactly the wrong conclusion.
//
// ⚠ THE CHANNEL LINES ARE GENERATED FROM THE RESOLVED CHANNELS, NOT WRITTEN OUT. The two are a
// company setting now (0.12.4 Part B); hardcoding the words "booking type" and "instructor" here
// would make this stale again the first time somebody changes a channel in the admin screen.
//
// ⚠ AND THE SWATCHES CALL chipVisual, the same function bookingRect paints with, rather than
// approximating it. Approximation is how the old legend drifted.
//
// ⚠ THE FLAGGED ITEM IS NOW FIXED, AND THE OLD NOTE HERE CONTRADICTED ITSELF. It claimed "Planned
// maintenance" was still drawn "the way it always was (accentLight, dashed)" and then said the
// swatch paints through chipVisual and shows the solid colour. The SECOND HALF is what the code
// does: the swatch is sample({ state: 'maintenance' }) like every other one, and chipVisual.ts:85
// paints that state as the SOLID KIND COLOUR. There is no dashed accentLight maintenance swatch
// and there has not been one since the legend was rebuilt.
//
// ⚠ AND THE LABEL WAS WRONG, WHICH IS WHY IT CHANGED (0.12.5 Part M). ChipState carries exactly
// ONE maintenance value (chipVisual.ts:38), reached whenever isMaintBooking(b) and a kind colour
// exist (DayView.tsx:517-523). So this single swatch is what a DEFECT booking looks like too, and
// calling it "Planned maintenance" named one of the two cases as though it were the only one.
// Ben's taxonomy, 2026-09-07: "Maintenance" is the PLANNED element and "Defect" is the SUDDEN or
// unexpected one. The label now names the MECHANISM instead of either case.
//
// ⚠ THE SCHEMA CANNOT YET TELL THEM APART, and that is deliberate, not an oversight to fix here.
// BookingKind carries only is_maintenance and is_unavailability (schema.prisma:1045-1046); there
// is NO is_defect. The two are distinguished today by the kind's NAME and its ui_color, and since
// the fill IS the kind colour, two kinds in two colours already render differently — which is
// exactly what the label now says. DO NOT ADD an is_defect flag or a second maintenance
// appearance: Ben ruled 2026-09-08 that Maintenance and Defect must both be trackable FOR FLEET
// RELIABILITY, which makes it structured data rather than a colour, and a future MEL-deferred case
// makes it a third state. That is design work, deliberately sequenced after this release.

/** What a channel is currently showing, in the words an operator would use. */
const CHANNEL_WORDS: Record<ChipChannelSource, string> = {
  kind: 'the booking type',
  instructor: 'the instructor',
  base: 'the base',
};

export function DayViewLegend({ chipChannels }: { chipChannels: ChipChannels }) {
  const pal = { pill: C.pill, red: C.red, green: C.green, amber: C.amber, textMuted: C.textMuted };
  // A representative ordinary chip: a confirmed booking flown by someone with a colour.
  const sample = (over: Partial<Parameters<typeof chipVisual>[0]>) => chipVisual({
    state: 'ordinary', fillColor: C.accent, kindColor: C.accent, unavailColor: UNAVAIL_COLOR,
    fillAlpha: FILL_ALPHA, pending: false, declined: false, outsideHours: false, confirmed: true,
    C: pal, ...over,
  });

  // A swatch painted exactly as the chip is: fill, border, dash, opacity — plus the top bar and
  // the status stripe where the real chip would draw them.
  const swatch = (v: ReturnType<typeof chipVisual>, opts: { topBar?: string | null } = {}) => (
    <span style={{
      position: 'relative', display: 'inline-block', width: 18, height: 12, borderRadius: 3,
      background: v.bg,
      border: `${v.strokeWidth}px ${v.strokeDasharray === 'none' ? 'solid' : 'dashed'} ${v.border}`,
      opacity: v.opacity, flex: '0 0 auto',
    }}>
      {opts.topBar && <span style={{ position: 'absolute', left: 1, right: 1, top: 0, height: 2, background: opts.topBar, borderRadius: 1 }} />}
      {v.statusStripe && <span style={{ position: 'absolute', left: 0, top: 1, bottom: 1, width: 3, background: v.statusStripe, borderRadius: 1 }} />}
    </span>
  );

  const row = (el: ReactNode, label: string) => (
    <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      {el}
      <span style={{ fontSize: 9, color: C.textMuted, fontFamily: F }}>{label}</span>
    </div>
  );

  // ── Group one: what the three colour channels mean ────────────────────────
  const channels: Array<[ReactNode, string]> = [
    [
      <span style={{ display: 'inline-block', width: 18, height: 12, borderRadius: 3, background: C.pill, border: `1px solid ${C.borderMed}`, position: 'relative', flex: '0 0 auto' }}>
        <span style={{ position: 'absolute', left: 0, top: 1, bottom: 1, width: 3, background: C.green, borderRadius: 1 }} />
      </span>,
      'Left bar: confirmation status',
    ],
    [
      <span style={{ display: 'inline-block', width: 18, height: 12, borderRadius: 3, background: C.pill, border: `1px solid ${C.borderMed}`, position: 'relative', flex: '0 0 auto' }}>
        <span style={{ position: 'absolute', left: 1, right: 1, top: 0, height: 2, background: C.accent, borderRadius: 1 }} />
      </span>,
      `Top bar: ${CHANNEL_WORDS[chipChannels.topBar]}`,
    ],
    [
      swatch(sample({ confirmed: false, outsideHours: false, declined: false })),
      `Background: ${CHANNEL_WORDS[chipChannels.fill]}`,
    ],
  ];

  // ── Group two: the states that OVERRIDE the fill and carry no status bar ──
  const states: Array<[ReactNode, string]> = [
    [swatch(sample({ pending: true })), 'Pending approval'],
    [swatch(sample({ state: 'provisional' })), 'Provisional (queued on this device)'],
    [swatch(sample({ state: 'conflict' })), 'Sync conflict'],
    // ⚠ NAMES THE MECHANISM, NOT ONE OF THE TWO CASES — see the note above the legend. There is a
    // single 'maintenance' ChipState and a Defect booking reaches it too, so "Planned maintenance"
    // was wrong for half of what this swatch represents.
    [swatch(sample({ state: 'maintenance' })), "Maintenance or defect (solid, in the kind's colour)"],
    [
      <span style={{ display: 'inline-block', width: 18, height: 12, borderRadius: 3, background: C.amberLight, border: `1px dashed ${C.amber}`, flex: '0 0 auto' }} />,
      'Defect / awaiting return',
    ],
    [
      <span style={{ display: 'inline-block', width: 18, height: 12, borderRadius: 3, background: C.amberLight, border: `1.5px dashed ${C.amber}`, flex: '0 0 auto' }} />,
      'Waitlisted (unallocated)',
    ],
    [
      // unassignedRect — indigo. It had NO legend entry at all before.
      <span style={{ display: 'inline-block', width: 18, height: 12, borderRadius: 3, background: '#eef2ff', border: '1.5px dashed #6366f1', flex: '0 0 auto' }} />,
      'Unassigned (recurring)',
    ],
    [
      <span style={{ display: 'inline-block', width: 18, height: 12, borderRadius: 3, background: '#f1f5f9', border: `1px dashed ${C.borderMed}`, opacity: 0.9, flex: '0 0 auto' }} />,
      'Away booking (other base)',
    ],
    [swatch(sample({ state: 'unavailable' })), 'Unavailable (resource blocked)'],
    [swatch(sample({ state: 'cancelled' })), 'Cancelled'],
    [
      // The amber phone badge (bookingRect). Painted with the same two colours the chip uses.
      // It is NOT a chip state — it rides on top of one — but it belongs in the reader's key.
      <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 6, background: C.amberLight, border: `1px solid ${C.amber}`, color: C.amber, fontSize: 8, lineHeight: '10px', textAlign: 'center', flex: '0 0 auto' }}>☎</span>,
      'Client needs a call',
    ],
  ];

  const groupLabel = (t: string) => (
    <span style={{ fontSize: 9, fontWeight: 700, color: C.textDim, fontFamily: F, letterSpacing: 0.3, textTransform: 'uppercase' }}>{t}</span>
  );

  return (
    <div style={{ marginTop: 12, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {groupLabel('What the colours mean')}
        {channels.map(([el, label]) => row(el, label))}
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', borderTop: `1px solid ${C.border}`, paddingTop: 6 }}>
        {groupLabel('Special states')}
        {states.map(([el, label]) => row(el, label))}
        <span style={{ fontSize: 9, color: C.textDim, fontFamily: F, fontStyle: 'italic' }}>
          These override the background and show no status bar.
        </span>
      </div>
    </div>
  );
}
